/**
 * @file Keeps the ww_d device id stable across cookie wipes: mirrors the cookie
 * into localStorage on page boot and, behind the deviceIdLocalStorageReseed flip,
 * writes it back when the browser has cleared it — so login device trust
 * (trusted_login_devices, skip-OTP) still matches on cookie-clearing devices.
 */
import { trackEvent } from '@@/bits/analytics'
import { getCookie, setCookie } from '@@/bits/cookie'
import { isAppUsing } from '@@/bits/flip'
import window from '@@/bits/global'
import { safeLocalStorage } from '@@/bits/safe_storage'

const DEVICE_ID_KEY = 'ww_d'
const FIFTY_YEARS_IN_SECONDS = 50 * 365 * 24 * 60 * 60

// Approximates Rails `domain: :all` (registrable domain) for our hosts.
// Host-only for single-label hosts and IP addresses.
function deviceIdCookieDomain(hostname: string): string | undefined {
  if (/^[\d.:]*$/.test(hostname)) return undefined
  const labels = hostname.split('.')
  if (labels.length < 2) return undefined
  return `.${labels.slice(-2).join('.')}`
}

function syncDeviceId(): void {
  const stored = safeLocalStorage.getItem(DEVICE_ID_KEY) ?? ''
  const cookie = getCookie(DEVICE_ID_KEY)

  if (stored !== '' && stored !== cookie) {
    if (!isAppUsing('deviceIdLocalStorageReseed')) return
    setCookie(DEVICE_ID_KEY, stored, {
      maxAgeInSeconds: FIFTY_YEARS_IN_SECONDS,
      domain: deviceIdCookieDomain(window.location.hostname),
    })
    trackEvent('Auth', 'Restored device id from localStorage')
  } else if (cookie !== '' && stored === '') {
    safeLocalStorage.setItem(DEVICE_ID_KEY, cookie)
  }
}

export { deviceIdCookieDomain, syncDeviceId }
